import colors from "@constants/colors"
import { body, bodyBold } from "@constants/typography"
import Link from "next/link"
import React from "react"
import { up } from "styled-breakpoints"
import styled from "styled-components"

export const Container = styled.a`
  flex-direction: column;
  display: flex;
  height: 100%;
  padding: 16px;

  ${up("laptop")} {
    padding: 20px;
    border: solid 1px transparent;

    &:hover {
      img {
        transform: scale(1.01);
      }

      border: solid 1px ${colors.divider};
    }
  }
`

const ImageWrapper = styled.div`
  width: 100%;
  height: calc(50vw - 36px);
  overflow: hidden;

  ${up("laptop")} {
    height: auto;
    flex-grow: 1;
  }
`

const ImageContainer = styled.div`
  position: relative;
  display: block;
  overflow: hidden;
  height: 100%;
`

const ImagePadding = styled.div`
  position: absolute;
  opacity: 0;
  padding-bottom: 100%;
`

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center center;
`

const Title = styled.div`
  ${bodyBold}
  margin: 20px 0 4px;
  color: ${colors.dark};
`

const Prices = styled.div`
  display: flex;
`

const Price = styled.div`
  ${body}
  color: ${colors.slateDark};
`

const CompareAtPrice = styled.div`
  ${body}
  margin-right: 6px;
  text-decoration: line-through;
  color: ${colors.slateMid};
`

const DiscountedPrice = styled.div`
  ${body}
  color: ${colors.primary};
`

const ClearanceTag = styled.div`
  ${body}
  top: 10px;
  left: 10px;
  background-color: red !important;
  color: white !important;
  padding: 5px 10px;
  border-radius: 3px;
  z-index: 1;
  width: fit-content !important;
`

export interface ProductCardProps {
  url: string
  image: string
  title: string
  price: string | number
  compareAtPrice?: string | number
  discountedPrice?: string | number
  isDiscounted?: string
  hidePrice?: boolean
  tags?: string[]
  productCategory: { name: string }[]
  /** Appended after price (e.g. " each"). Used on Pro Series list when soldAs === "single". */
  priceSuffix?: string
}

const ProductCard: React.FC<ProductCardProps> = ({
  url,
  image,
  title,
  price,
  compareAtPrice,
  discountedPrice,
  isDiscounted,
  hidePrice,
  tags = [],
  productCategory,
  priceSuffix,
}) => {
  let cleanedCompareAtPrice
  let cleanedPrice

  if (isDiscounted == "yes" && discountedPrice) {
    cleanedPrice = Number.parseFloat(
      discountedPrice.toString().replace("$", "").replace(",", "")
    )
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  } else {
    cleanedPrice = Number.parseFloat(
      price.toString().replace("$", "").replace(",", "")
    )
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  }

  if (isDiscounted == "yes") {
    cleanedCompareAtPrice =
      price &&
      Number.parseFloat(price.toString().replace("$", "").replace(",", ""))
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  } else {
    cleanedCompareAtPrice =
      compareAtPrice &&
      Number.parseFloat(
        compareAtPrice.toString().replace("$", "").replace(",", "")
      )
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  }

  const isClearance =
    productCategory.some((category) => category.name === "Clearance") ||
    tags.includes("Clearance")

  return (
    <Link href={url} passHref>
      <Container>
        {isClearance && <ClearanceTag>CLEARANCE</ClearanceTag>}
        <ImageWrapper>
          <ImageContainer>
            <ImagePadding />
            <Image src={image} className="DatoImage" />
          </ImageContainer>
        </ImageWrapper>
        <Title>{title}</Title>
        {!hidePrice && cleanedCompareAtPrice && (
          <Prices>
            <CompareAtPrice>${cleanedCompareAtPrice}</CompareAtPrice>
            <DiscountedPrice>
              ${cleanedPrice}
              {priceSuffix}
            </DiscountedPrice>
          </Prices>
        )}
        {!hidePrice && !cleanedCompareAtPrice && (
          <Price>
            ${cleanedPrice}
            {priceSuffix}
          </Price>
        )}
      </Container>
    </Link>
  )
}

export default ProductCard
